import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/app/Bits";
import { Calendar, Heart, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * Standalone gestation calculator. Pick a breeding date → returns the canonical
 * 63-day timeline with key milestones. Doesn't require an actual litter — useful
 * for planning future breedings.
 */
export default function GestationCalculatorPage() {
  const [bredOn, setBredOn] = useState("");
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!bredOn) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/gestation/calculator", { params: { bred_on: bredOn } });
      setTimeline(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not calculate timeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Default to today so the calculator shows results immediately.
    const today = new Date().toISOString().slice(0, 10);
    setBredOn(today);
  }, []);

  useEffect(() => {
    if (bredOn) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bredOn]);

  const fmtRange = (range) => Array.isArray(range) ? `${range[0]} → ${range[1]}` : "—";

  return (
    <div className="space-y-8" data-testid="gestation-calculator-page">
      <SectionTitle overline="Breeder · Planning" title="63-day gestation calculator" />

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 space-y-4">
          <form onSubmit={submit} className="flex flex-wrap items-end gap-3" data-testid="gestation-form">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="bred-on">Last breeding date</Label>
              <Input
                id="bred-on"
                type="date"
                value={bredOn}
                onChange={(e) => setBredOn(e.target.value)}
                className="mt-1.5"
                required
                data-testid="gestation-bred-on"
              />
            </div>
            <Button type="submit" disabled={loading} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="gestation-submit">
              {loading ? "Calculating…" : "Calculate"}
            </Button>
          </form>
          {error && <p className="text-sm text-destructive" data-testid="gestation-error">{error}</p>}
        </CardContent>
      </Card>

      {timeline && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gestation-timeline">
          <Card className="border-primary bg-secondary shadow-none rounded-lg">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Expected whelp</p>
              <p className="font-display text-3xl font-extrabold tracking-tighter mt-2 text-primary" data-testid="gestation-expected">
                {timeline.expected_whelp_day_63}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Day 63 after breeding · {timeline.days_remaining >= 0 ? `${timeline.days_remaining} days from today` : `${Math.abs(timeline.days_remaining)} days ago`}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Whelp watch window</p>
              <p className="font-display text-xl font-bold mt-2" data-testid="gestation-whelp-window">
                {fmtRange(timeline.whelp_window_day_58_65)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Days 58–65 — be ready</p>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Heart size={18} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Ultrasound</p>
                  <p className="text-sm text-muted-foreground mt-1">{fmtRange(timeline.ultrasound_day_28_30)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Days 28–30 · confirm pregnancy</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Calendar size={18} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">X-ray</p>
                  <p className="text-sm text-muted-foreground mt-1">{fmtRange(timeline.xray_day_45_55)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Days 45–55 · count puppies</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-dashed border-border shadow-none rounded-lg">
        <CardContent className="p-6 flex items-start gap-3">
          {timeline?.days_remaining > 0 && timeline.days_remaining <= 7 ? (
            <AlertCircle size={18} className="text-[#CC9C33] shrink-0 mt-0.5" />
          ) : (
            <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
          )}
          <div className="text-sm text-foreground/80">
            <strong>Track this breeding as a litter</strong> to log puppies, daily weights, ENS, and socialization milestones automatically.
            <Link to="/breeder/litters" className="ml-2 text-primary underline underline-offset-4" data-testid="gestation-to-litters">
              Open Litters →
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
